<template>
  <!-- eslint-disable max-len -->
  <BaseCard
    :id="`dataset-${dataset.name}`"
    ref="card"
    :title="cardTitle"
    class="dataset-card"
    :class="[
      { 'dataset-selected': selected }
    ]"
    @edit-mode="(mode) => editTitle = mode"
    @apply-edit="handleApplyEdit"
  >
    <template #title-start>
      <input
        type="checkbox"
        class="dataset-checkbox"
        :checked="selected"
        @change="toggleSelection($event)"
      />
      <div v-tooltip="getDatasetStatusMessage">
        <SVGIcon
          :iconName="getDatasetStatusIcon"
          class="dataset-status-icon"
          :class="getDatasetStatus"
          :width="'28px'"
          :height="'28px'"
        />
      </div>
    </template>
    <template #header>
      <div class="icon-container">
        <IconButton
          v-if="!isParked"
          v-tooltip="`Refresh ${dataset.name}`"
          class="refresh-icon"
          :icon="'refresh'"
          :width="22"
          :height="22"
          :type="''"
          @click.stop="handleRefreshClicked"
        />
        <ActionsButton
          :actions="contextMenuActions"
          @action-clicked="handleDatasetCardAction"
        />
      </div>
    </template>


  <!-- CARD BODY -->

  <template #body>
    <div class="card-body">
      <div v-if="!isParked" class="left-panel">

          <BaseTextExpander v-if="dataset.description" :text="dataset.description" class="mt-2" />
          <div class="card-detail-box-row mt-2">
            <div class="card-detail-box card-detail-box--image">
              <div v-tooltip="'Click to go to Gallery'" class="image-container" @click="handleGalleryClicked">
                <img 
                  :src="`/v3/enterprise/dataset-image/${dataset.id}`" 
                  :alt="dataset.name || 'Dataset image'"
                  class="dataset-image"
                  @error="$event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE2MCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE0IiBmb250LWZhbWlseT0iQXJpYWwiIGZpbGw9IiM5OTkiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZXM8L3RleHQ+PC9zdmc+'"
                />
                <div class="image-count-badge">
                  {{ dataset.numImages || 0 }}
                </div>
              </div>
            </div>
            <div class="card-detail-box-column">
              <div class="card-detail-box">
                <div class="card-detail-box__badge">
                  <div class="card-detail-box__icon">
                    <SVGIcon
                      :iconName="'tag'"
                      :width="'24px'"
                      :height="'24px'"
                    />
                  </div>
                  <div class="card-detail-box__details">
                    <h3 class="card-detail-box__details-title">Labels</h3>
                    <span class="card-detail-box__details-value">{{ dataset.numLabels ? dataset.numLabels : '--' }}</span>
                    <div v-if="canWrite && !isParked" class="card-detail-box__icon-container">
                      <DatasetCardLabelsPopper
                        v-if="!isParked"
                        :dataset="dataset"
                        :labels="labels"
                        :canWrite="canWrite"
                        :permissions="permissions"
                        @delete-label="(label) => handleDeleteLabelClicked(label)"
                        @update-label="$emit('update-label')"
                        @label-created="$emit('label-created')"
                      />
                      <IconButton

                        v-tooltip="'Add new labels from an array of string'"
                        class="create-label-icon"
                        :icon="'add'"
                        :width="20"
                        :height="20"
                        :type="''"
                        :disabled="isFrozen"
                        @click.stop="createLabel"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <template v-if="!isParked">
                <div class="card-detail-box _groups">
                  <div 
                    div v-tooltip="`Groups are useful to separate images into 'train' and 'validate' groups for workflow.`"
                    class="card-detail-box__badge _bottom-border">
                   
                    <div class="card-detail-box__details">
                      <h3 class="card-detail-box__details-title">Groups: {{ dataset.groups ? dataset.groups.length : '--' }}</h3>
                      <div class="card-detail-box__icon-container">
                        <IconButton
                          v-if="canWrite"
                          class="create-annset-icon"
                          :icon="'add'"
                          :width="20"
                          :height="20"
                          :type="''"
                          :disabled="isFrozen"
                          @click.stop="createGroupClicked"
                        />
                      </div>
                    </div>
                  </div>
                  <div v-if="dataset.groups && dataset.groups.length > 0" class="group-li-container scrollbar">
                    <GroupListItem
                      v-for="(g,i) in dataset.groups"
                      :key="i"
                      :name="g.name"
                      :count="g.count"
                      @delete-group="$emit('delete-group', g)"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>
          <template v-if="checkDockerTaskProgress()">
            <TaskStatusBars :task="dataset.docker_task" class="status-bars" />
          </template>
          <template v-else>
            <template v-if="!isParked">
              <div class="card-detail-box _groups card-detail-box--annset" >
                <div class="card-detail-box__badge _bottom-border">
                  <div
                    v-tooltip="`Annotation sets are collections of annotation and sensor data for images in the dataset.
                  One image can have annotations and sensro data that belong to different annotation sets.
                  This is usefull for comparing outputs from different sources.`"
                    class="card-detail-box__icon"
                  >
                    <SVGIcon
                      :iconName="'set'"
                      :width="'24px'"
                      :height="'24px'"
                    />
                  </div>
                  <div class="card-detail-box__details">
                    <h3 class="card-detail-box__details-title">Annotation Sets</h3>
                    <span class="card-detail-box__details-value">{{ numAnnotationSets }}</span>
                    <div class="card-detail-box__icon-container">
                      <!-- <IconButton
                      v-if="isOwner || isEditor || isAdmin"
                      class="create-annset-icon"
                      :icon="'export'"
                      :width="20"
                      :height="20"
                      :type="''"
                      :disabled="isFrozen"
                      @click.stop="importAnnotationSet"
                    /> -->
                      <IconButton
                        v-if="canWrite"
                        class="create-annset-icon"
                        :icon="'add'"
                        :width="20"
                        :height="20"
                        :type="''"
                        :disabled="isFrozen"
                        @click.stop="createAnnotationSetClicked"
                      />
                    </div>
                  </div>
                </div>
                <div v-if="!annotationSetsLoading" class="annset-li-container scrollbar">
                  <AnnotationSetListItem
                    v-for="(s,i) in internalAnnotationSets"
                    :key="i"
                    v-model:numImg="s.num_images"
                    v-model:numAnnotations="s.num_annotations"
                    v-model:hashId="s.hashed_id"
                    :annotationSet="s"
                    :isFrozen="isFrozen"
                    :canWrite="canWrite"
                    @copy-annset="handleCopyAnnSetClicked(s)"
                    @delete-annset="$emit('delete-annset', s)"
                    @open-conf-matrix="$emit('open-conf-matrix', s)"
                    @open-description="$emit('open-description', s)"
                    @generate-hash="$emit('generate-hash', s)"
                  />
                </div>
                <div v-else class="loading-container">
                  <InlineLoader :width="'30px'" :height="'30px'" :border="'7px'" />
                </div>
              </div>
            </template>
          </template>
        
      </div>
      <div v-else class="parked-dataset-status">
        <SVGIcon
          class="parking-icon"
          :iconName="'parking'"
          :width="'50%'"
          :height="'50%'"
        />
        <p class="date-text">Parked Date: {{ formatTime(dataset.parked_at) }}</p>
      </div>
    </div>    
  </template>
    <template #footer>
      <div class="footer">
        <EncodedIdField
          :id="dataset.id"
          :type="'dataset'"
        />

        <template v-if="getDatasetStatus === 'warning'">
          <button v-tooltip="'Click to dismiss error. Dataset status will reset.'" class="button mt-2" @click="handleWarning">Clear error</button>
        </template>
        <template v-else>
          <div>
            <div class="footer-icons">
              <SVGIcon
                v-if="is3d"
                v-tooltip="'This dataset has 3D elements'"
                :iconName="'3d'"
                :width="'24px'"
                :height="'24px'"
                class="footer-icon ms-2"
              />

              <SVGIcon
                v-if="!canWrite"
                v-tooltip="'Read Only Dataset - Copy to another dataset to make changes or add trainers'"
                :iconName="'pencil_off'"
                :width="'24px'"
                :height="'24px'"
                class="footer-icon ms-2"
              />

              <SVGIcon
                v-if="isPublic"
                v-tooltip="'Public Dataset - Everyone can view this dataset'"
                :iconName="'earth'"
                :width="'24px'"
                :height="'24px'"
                class="footer-icon ms-2"
              />
            </div>
            <div v-if="dataset.size > 0" class="dataset-size">
              Size: {{ formatBytes(dataset.size) }}
            </div>
          </div>
        </template>
      </div>
    </template>
  </BaseCard>
</template>


<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

import BaseCard from '@/components/BaseCard.vue';
import ActionsButton from '@/components/ActionsButton.vue';
import BaseTextExpander from '@/components/BaseTextExpander.vue';
import AnnotationSetListItem from '@/components/DatasetComponent/DatasetManagement/AnnotationSetListItem.vue';
import GroupListItem from '@/components/DatasetComponent/DatasetManagement/GroupListItem.vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import TaskStatusBars from '@/components/TaskStatusBars.vue';
import { uppercaseFirstLetter } from '@/assets/js/utils.js';
import IconButton from '../../IconButton.vue';
import DatasetCardLabelsPopper from '../DatasetCardLabelsPopper.vue';
import SVGIcon from '../../SVGIcon.vue';
import InlineLoader from '../../InlineLoader.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface'

// ---------- props ----------
const props = defineProps({
  dataset: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  annotationSets: { type: Array, default: () => [] },
  labels: { type: Array, default: () => [] },
  permissions: { type: Object, default: () => ({}) },
})

// ---------- emits ----------
const emit = defineEmits([
  'delete-dataset','copy-dataset','copy-annset','clicked','create-annset',
  'delete-annset','create-label','deleted-label','refresh',
  'update:annotation-sets','update:labels','open-dataset-access-management',
  'edit-dataset','edit-annset','open-export','open-import','open-upload',
  'freeze-dataset','import-annset','create-groups','delete-group',
  'to-gallery','open-analytics','open-map','generate-token',
  'open-conf-matrix','open-description',
  'update-label','label-created','add-snapshot','dismiss-error','generate-hash',
  'dataset-selected',
])

// ---------- store / router ----------
const store = useStore()
const router = useRouter()

// ---------- state ----------
const dsConn = new DatastoreConnect(store.state.enterpriseServerUrl)
const loading = ref(false)
const annotationSetsLoading = ref(false)
const editTitle = ref(false)
const statusType = ref('')
const is3d = ref(false)

// ---------- computed ----------
const currentProject = computed(() => store.state.projects.currentProject)
const datasetNotifications = computed(
  () => store.state.notifications.datasets?.[props.dataset.id]
)
const user = computed(() => store.state.user.user)

const canWrite = computed(() => props.permissions?.dataset_write)
const canRead = computed(() => props.permissions?.dataset_read)
const isAdmin = computed(() => ['super','admin'].includes(user.value?.role))
const isPublic = computed(() => currentProject.value?.is_public)
const isFrozen = computed(() => props.dataset?.is_frozen)
const isDebugMode = computed(() => store.state.debugMode)
const isParked = computed(
  () => props.dataset?.parked_at !== '0001-01-01T00:00:00Z'
)

const internalAnnotationSets = computed({
  get: () => props.annotationSets,
  set: v => emit('update:annotation-sets', v)
})

const cardTitle = computed(() => {
  if (!props.dataset?.name) return ''
  return isDebugMode.value
    ? `(${props.dataset.id}) ${props.dataset.name}`
    : props.dataset.name
})

const numAnnotationSets = computed(
  () => internalAnnotationSets.value?.length || 0
)

const getDatasetStatusIcon = computed(() => {
  const task = props.dataset?.docker_task
  if (!task) return 'check_circle'
  if (task.status === 'complete') return 'check_circle'
  if (['error','terminated'].includes(task.status)) return 'warning'
  return 'ellipsis'
})

const getDatasetStatus = computed(() => {
  const task = props.dataset?.docker_task
  if (!task) return 'available'
  if (task.status === 'complete') return 'available'
  if (['error','terminated'].includes(task.status)) return 'warning'
  return 'running'
})

const getDatasetStatusMessage = computed(() => {
  const task = props.dataset?.docker_task
  if (!task) return 'Available'
  if (task.status === 'complete') return 'Available'
  return task.status ? task.status[0].toUpperCase() + task.status.slice(1) : ''
})

const contextMenuActions = computed(() => {
  const actions = []
  
  if (!isParked.value && canWrite.value) {
    actions.push({ icon: 'edit', label: 'Edit Info', eventKey: 'edit', disabled: isFrozen.value, tooltip: 'Edit dataset name and description' })
  }
  
  if (!isParked.value && canWrite.value) {
    actions.push({ icon: 'import', label: 'Import', eventKey: 'import', disabled: isFrozen.value, tooltip: 'Import data to dataset' })
  }
  
  if (!isParked.value && (canWrite.value || canRead.value)) {
    actions.push({ icon: 'export', label: 'Export', eventKey: 'export', tooltip: 'Download data from dataset' })
  }
  
  if (!isParked.value && canWrite.value) {
    actions.push({ icon: 'database_sync', label: 'Create Snapshot', eventKey: 'snapshot', disabled: isFrozen.value, tooltip: 'Create snapshot from dataset' })
  }
  
  if (!isParked.value && (canWrite.value || canRead.value)) {
    actions.push({ icon: 'copy_white', label: 'Copy Dataset', eventKey: 'copy', disabled: isFrozen.value, tooltip: 'Copy images from source dataset to destination' })
  }
  
  if (!isParked.value && (canWrite.value || canRead.value)) {
    actions.push({ icon: 'insights', label: 'Analytics', eventKey: 'analytics', tooltip: 'View dataset analytics' })
  }
  
  if (!isParked.value && (canWrite.value || canRead.value)) {
    actions.push({ icon: 'map', label: 'View on Map', eventKey: 'map', tooltip: 'View dataset images on map. This is only supported when images have location metadata' })
  }
  
  if (!isParked.value) {
    actions.push({ icon: 'history', label: 'History', eventKey: 'history', tooltip: 'View dataset history' })
  }
  
  return actions
})

// ---------- watchers ----------
watch(
  () => props.dataset,
  d => {
    if (d?.annotation_sets) {
      internalAnnotationSets.value = d.annotation_sets
    }
  },
  { deep: true }
)

// ---------- methods ----------
function checkDockerTaskProgress() {
  const t = props.dataset?.docker_task
  return t && !t.status.toLowerCase().includes('complete')
}

function handleWarning() {
  const t = props.dataset?.docker_task
  if (['error','terminated'].includes(t?.status)) {
    emit('dismiss-error')
  }
}

function formatTime(str) {
  if (!str) return ''
  return new Date(str).toLocaleString('default', {
    year:'numeric', month:'short', day:'numeric',
    hour:'numeric', minute:'numeric', hour12:true
  })
}

function navigateToHistory() {
  router.push({ name: 'dataset.history', params: { id: props.dataset.id } })
}

const emitOnly = name => () => emit(name)

const handleCopyClicked = emitOnly('copy-dataset')
const handleOpenSnapshot = emitOnly('add-snapshot')
const deleteDataset = emitOnly('delete-dataset')
const createAnnotationSetClicked = emitOnly('create-annset')
const handleRefreshClicked = emitOnly('refresh')
const handleGalleryClicked = emitOnly('to-gallery')
const handleEditClicked = emitOnly('edit-dataset')
const importAnnotationSet = emitOnly('import-annset')
const createGroupClicked = emitOnly('create-groups')
const generateAPIToken = emitOnly('generate-token')

function handleCopyAnnSetClicked(set) {
  emit('copy-annset', set)
}

function handleDeleteLabelClicked(label) {
  emit('deleted-label', label)
}

function handleClickEvent() {
  emit('clicked')
}

function createLabel() {
  emit('create-label', props.dataset)
}

function handleManageAccess() {
  emit('open-dataset-access-management', props.dataset)
}

function openImportDataset() { emit('open-import') }
function openUploadDataset() { emit('open-upload') }
function openExportDataset() { emit('open-export') }

function openAnalytics() {
  store.commit('datasets/setCurrentDataset', props.dataset)
  router.push({ name: 'datasets.statistics', params: { projectId: currentProject.value.id } })
}

function openMap() {
  store.commit('datasets/setCurrentDataset', props.dataset)
  router.push({ name: 'datasets.map', params: { projectId: currentProject.value.id } })
}

function handleDatasetCardAction(eventKey) {
  switch(eventKey) {
    case 'edit':
      handleEditClicked()
      break
    case 'import':
      openUploadDataset()
      break
    case 'export':
      openExportDataset()
      break
    case 'snapshot':
      handleOpenSnapshot()
      break
    case 'copy':
      handleCopyClicked()
      break
    case 'analytics':
      openAnalytics()
      break
    case 'map':
      openMap()
      break
    case 'history':
      navigateToHistory()
      break
  }
}

function handleApplyEdit(name) {
  emit('edit-dataset', name)
}

function handleFreezeDataset(isFrozen) {
  emit('freeze-dataset', isFrozen)
}

function formatBytes(bytes) {
  if (!bytes) return '0 Bytes'
  const units = ['Bytes','KB','MB','GB','TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1000))
  return `${(bytes / 1000 ** i).toFixed(2)} ${units[i]}`
}

function toggleSelection(event) {
  console.log('Checkbox clicked for dataset:', props.dataset.id, 'Checked:', event.target.checked);
  emit('dataset-selected', {
    id: props.dataset.id,
    dataset: props.dataset,
    checked: event.target.checked,
  })
}
</script>


<style lang="scss" scoped>

.dataset-status-icon {
  margin-right: 16px;

  &.available {
    @include themify() {
      color: themed('color-status-complete') !important;
      cursor: default;
    }
  }

  &.warning {
    @include themify() {
      color: themed('color-status-warning') !important;
      cursor: pointer;
    }
  }

  &.running {
    @include themify() {
      color: themed('color-status-running') !important;
      cursor: default;
    }
  }
}

.dataset-card {
  margin: 2px;
}

.card-body {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.left-panel {
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.dataset-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-count-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #fbbf24;
  color: #000;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  min-width: 32px;
  text-align: center;
}

.image-container {
  position: relative;
  width: 100%;
  height: 140px;
  overflow: hidden;
  border-radius: 4px;
  background: #f5f5f5;
  object-fit: cover;
  cursor: pointer;
}

.dataset-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-count-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #eac213;
  color: #000;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  min-width: 32px;
  text-align: center;
}

.card:not([disabled="true"]):hover {
  @include themify() {
    outline: 2px solid themed('card-border-color-hover');
  }
}
.card:not([disabled="true"]).dataset-selected {
  // margin: 3px;
  @include themify() {
    outline: 3px solid themed('card-list-color-selected-border')
  }
}

.icon-container {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
  margin-bottom: 0;
  button + button {
    margin-left: 10px;
  }
  @include themify() {
    color: themed('body-text-color-secondary');
  }
}

.card .create-annset-icon {
  margin-left: 4px;
  @include themify() {
    color: themed('icon-color-primary');
  }
}

.card .create-label-icon {
  @include themify() {
    color: themed('icon-color-primary');
  }
}

.card:hover .copy-icon:hover,
.card:hover .create-annset-icon:hover,
.card:hover .refresh-icon:hover {
  @include themify() {
    color: themed('icon-color-primary');
  }
}

.annset-li-container {
  overflow-y: auto;
  font-size: 0.875rem;
  flex: 1 1;
}

.group-li-container {
  width: 100%;
  padding: 4px;
  overflow-y: auto;
}

.loading-container {
  display: flex;
  flex: 1 1;
  align-items: center;
  justify-content: center;
}

.secondary-title-container {
  display: flex;
  margin-top: 7px;
  align-items: center;
  line-height: 1;
}

.secondary-title {
  font-weight: bold;
  @include themify() {
    font-size: 0.875rem;
    color: themed('body-text-color-secondary');
  }
}

$badge-height: 50px;
$badge-padding: 4px;
.card-detail-box-row {
  display: flex;
  flex-direction: row;
  min-height: $badge-height;
  gap: 4px;
}

.card-detail-box-column {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.card-detail-box {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0,0,0,0.075);
  border-radius: 4px;
  @include themify() {
    background: themed('color-white-600');
  }

  &__badge {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    height: fit-content;
    width: 100%;
    padding: $badge-padding;

    &._bottom-border {
      border-bottom: 1px solid rgba(0, 0, 0, 0.075);
    }
  }

  &__icon {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    min-width: $badge-height - ($badge-padding * 2) - 2px;
    height: 100%;
    @include themify() {
      background: themed('color-primary');
    }

    svg {
      color: white;
    }
  }

  &__details {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    height: 100%;
    border-radius: 4px;
    margin-left: 12px;
    overflow: hidden;
  }

  &__details-title {
    line-height: 1;
    font-weight: 500;
    @include themify() {
      font-size: 0.75rem;
      color: themed('body-text-color-secondary');
    }
  }

  &__details-value {
    margin-top: auto;
    line-height: 1;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    @include themify() {
      font-size: 1.125rem;
      color: themed('body-text-color');
    }
  }

  &__icon-container {
    position: absolute;
    top: 4px;
    right: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }

  &._groups {
    flex: 1;
  }
}

.card-detail-box--image {
  flex: 0 0 48%;
  overflow: hidden;
  padding: 0 !important;
  height: auto;
 
}

.card-detail-box--annset {
  margin-top: 4px; 
  height: 130px;
}

.card-detail-box-column .card-detail-box._groups {
  max-height: 130px;
}

.parked-dataset-status {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .parking-icon {
    color: themed('icon-color-primary');
    opacity: 0.3
  }

  p {
    @include themify() {
      font-size: 0.9rem;
      color: themed('body-text-color');
    }
  }
}

.status-bars {
  padding: 15px;
}

.footer {
  display: flex;
  flex-direction: row;
  justify-content:space-between;
  align-items: center;
  width: 100%;
}

.footer-icons {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: fit-content;
  justify-content: flex-end;
  margin-top: 5px;
}

.footer-icon {
  color: var(--color-primary);
}

.button {
  background: white;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 26px;
  padding: 0;
  box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
  padding: 4px 8px;
  gap: 2px;
  width: fit-content;

  @include themify() {
    color: themed('body-text-color')
  }

  &:hover {
    background: rgba(var(--color-primary-100-rgb), 0.5);
  }
}

.dataset-size {
  font-size: 0.8rem;
  color: var(--body-text-color-secondary);
}

.dataset-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin-right: 8px;
  flex-shrink: 0;
  accent-color: var(--color-primary);
  appearance: checkbox;
  align-self: center;
}

.dataset-checkbox:hover {
  transform: scale(1.1);
}

</style>
